import React from 'react';
import moment from 'moment';
import { isMobileLocation } from '../../../util/locationUtils';
import ServiceItemsList from './ServiceItemsList';
import openNavigation from '../../../util/openNavigation';
import serviceItemsService from '../../../services/serviceItemsService';

// Compact request panel rendered on mobile installer screens instead of the
// long RequestimateState detail panel. Used when a deep-link (Today, Tasks,
// Reminders) lands on /requestimates with a selectedRequestId.
//
// Shape mirrors the MyWork list-card pattern:
//   Header band — customer + vehicle + (address strip + Call/Directions if
//   on a mobile-installer location)
//   Body — each service with its ServiceItemsList (steps/materials)
//
// Read-only for non-technicians; technicians get work-step toggles. After a
// toggle, fires the parent's onRefresh callback so the page can re-pull
// services with updated item statuses.

class TechRequestPanel extends React.Component {
  handleToggleItem = async (item) => {
    if (!item || !item.id) return;
    try {
      await serviceItemsService.completeItemWithMaterials(item.id);
      if (this.props.onRefresh) this.props.onRefresh();
    } catch (err) {
      // Silent — next refresh will surface the unchanged state.
    }
  };

  render() {
    const { request, services, currentLocation, user } = this.props;

    if (!request) {
      return (
        <div className="container py-4">
          <div className="alert alert-warning">
            <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2" />
            Request not found.
          </div>
        </div>
      );
    }

    const isMobile = isMobileLocation(currentLocation);
    const role = (user?.role || '').toLowerCase();
    const isTech = role === 'technician';
    const currentUserId = user?.User_id;

    const customerName = request.customer_name || request.dealerName || 'Customer';
    const customerPhone = request.customer_phone || request.phone_number || '';
    const vehicle = [request.Year, request.Make, request.Model].filter(Boolean).join(' ');
    const address = request.customer_address || '';
    const svcList = Array.isArray(services) ? services : (Array.isArray(request.services) ? request.services : []);

    const handleDirections = () => openNavigation({
      address,
      lat: request.customer_address_lat,
      lng: request.customer_address_long,
    });

    const scheduledDisplay = request.scheduledStart
      ? moment.utc(request.scheduledStart).format('ddd, MMM D · h:mm A')
      : (request.availableDate
          ? moment.utc(request.availableDate).format('ddd, MMM D')
          : null);

    return (
      <div className="tech-request-panel" style={{ maxWidth: 640 }}>
        <div
          className="card"
          style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderLeft: `4px solid ${request.request_type === 'dealer' ? '#fd7e14' : '#007bff'}`,
            borderRadius: 8,
          }}
        >
          {/* Header band */}
          <div className="card-body py-3">
            <div style={{ fontWeight: 700, fontSize: 18 }}>{customerName}</div>
            {vehicle && (
              <div className="text-muted" style={{ fontSize: 14 }}>
                {request.Color && <span className="mr-1">{request.Color}</span>}
                {vehicle}
                {request.VIN && <span className="ml-1 font-monospace small">· {request.VIN}</span>}
              </div>
            )}
            {scheduledDisplay && (
              <div className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>
                <i className="fa-sharp fa-regular fa-calendar-alt mr-1" aria-hidden="true" />
                {scheduledDisplay}
              </div>
            )}

            {/* Address + quick actions — mobile-installer locations only */}
            {isMobile && address && (
              <div className="mt-2 pt-2" style={{ borderTop: '1px solid #f3f4f6' }}>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  <i className="fa-sharp fa-regular fa-location-dot mr-1" aria-hidden="true" />
                  {address}
                </div>
                <div className="d-flex justify-content-around mt-2">
                  {customerPhone && (
                    <a className="btn btn-link btn-sm text-decoration-none" href={`tel:${customerPhone}`}>
                      <i className="fa-sharp fa-regular fa-phone mr-1" aria-hidden="true" />
                      Call
                    </a>
                  )}
                  <button type="button" className="btn btn-link btn-sm text-decoration-none" onClick={handleDirections}>
                    <i className="fa-sharp fa-regular fa-diamond-turn-right mr-1" aria-hidden="true" />
                    Directions
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Services */}
          {svcList.length > 0 && (
            <div className="card-body pt-0 border-top">
              {svcList.map((svc, idx) => {
                const category = svc.service_category || svc.category;
                const desc = svc.custom_description || svc.description;
                const heading = category && desc
                  ? `${category} - ${desc}`
                  : (category || desc || `Service ${idx + 1}`);
                return (
                  <div key={svc.id || svc.request_service_id || idx} className="mb-3">
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{heading}</div>
                    <ServiceItemsList
                      items={svc.items || []}
                      currentUserId={currentUserId}
                      serviceState={svc.service_state}
                      totalTaskCount={svc.tech_assignment?.total_task_count || (svc.items || []).length}
                      onToggleItem={isTech ? this.handleToggleItem : undefined}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default TechRequestPanel;
